import {
  type Highlight,
  type RectAnchor,
  highlightClass,
  makeRectAnchor,
} from "../model/highlights.ts";

/**
 * Resaltados sobre un PDF.
 *
 * Se seleccionan spans invisibles de la capa de texto, y de esa selección se
 * guardan los RECTÁNGULOS que ocupa, normalizados 0..1 respecto de la página.
 *
 * Normalizar es lo que hace que la marca sobreviva al zoom y a la resolución:
 * "el 61% del ancho, arrancando en el 12%" vale igual dibujando la página a 1x
 * que a 3x. Y como un PDF no cambia nunca, esa posición es válida para siempre
 * — no hay nada que reubicar ni huérfanos que perseguir.
 */

/** Cada página del lector lleva esta clase y su índice en `data-page`. */
export const PAGE_CLASS = "pdf-page";

type Rect = [number, number, number, number];

/** Recorta a 0..1 y descarta lo degenerado. */
function clampRect(x: number, y: number, w: number, h: number): Rect | null {
  const x0 = Math.max(0, Math.min(1, x));
  const y0 = Math.max(0, Math.min(1, y));
  const x1 = Math.max(0, Math.min(1, x + w));
  const y1 = Math.max(0, Math.min(1, y + h));
  const rw = x1 - x0;
  const rh = y1 - y0;
  // Un recuadro de menos del 0.5% de la página es un clic, no una marca.
  return rw > 0.005 && rh > 0.005 ? [x0, y0, rw, rh] : null;
}

/**
 * Ancla de ÁREA: un recuadro dibujado a mano sobre una página, en coordenadas
 * de pantalla.
 *
 * Es lo que cubre lo que la capa de texto no puede — fórmulas, gráficos, y
 * PDFs escaneados, donde directamente no hay texto que seleccionar.
 */
export function areaAnchor(
  pageEl: HTMLElement,
  a: { x: number; y: number },
  b: { x: number; y: number },
): RectAnchor | null {
  const pageIndex = Number(pageEl.dataset.page);
  if (!Number.isInteger(pageIndex)) return null;

  const pr = pageEl.getBoundingClientRect();
  if (pr.width <= 0 || pr.height <= 0) return null;

  const left = Math.min(a.x, b.x) - pr.left;
  const top = Math.min(a.y, b.y) - pr.top;
  const rect = clampRect(
    left / pr.width,
    top / pr.height,
    Math.abs(a.x - b.x) / pr.width,
    Math.abs(a.y - b.y) / pr.height,
  );
  return rect ? makeRectAnchor(pageIndex, [rect], "", true) : null;
}

function intersects(a: DOMRect, b: DOMRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Texto de la selección acotado a una página (vacío si no se puede acotar). */
function quoteWithin(range: Range, pageEl: HTMLElement): string {
  try {
    const clipped = range.cloneRange();
    const page = document.createRange();
    page.selectNodeContents(pageEl);
    if (clipped.compareBoundaryPoints(Range.START_TO_START, page) < 0) {
      clipped.setStart(page.startContainer, page.startOffset);
    }
    if (clipped.compareBoundaryPoints(Range.END_TO_END, page) > 0) {
      clipped.setEnd(page.endContainer, page.endOffset);
    }
    return clipped.toString().trim();
  } catch {
    return "";
  }
}

/**
 * Anclas para la selección actual, UNA POR PÁGINA que toque.
 *
 * Una frase que cruza un salto de página es normal en un PDF, y devolver un
 * ancla por página deja que cada una se dibuje contra su propia geometría en
 * vez de inventar un rectángulo que cruce el corte.
 */
export function selectionAnchors(root: HTMLElement): RectAnchor[] {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return [];

  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return [];

  // Los rectángulos de altura o ancho ~0 son artefactos de los bordes de la
  // selección; pintarlos deja rayitas sueltas.
  const clientRects = [...range.getClientRects()].filter((r) => r.width > 0.5 && r.height > 0.5);
  if (clientRects.length === 0) return [];

  const out: RectAnchor[] = [];
  for (const pageEl of root.querySelectorAll<HTMLElement>(`.${PAGE_CLASS}`)) {
    const pageIndex = Number(pageEl.dataset.page);
    if (!Number.isInteger(pageIndex)) continue;

    const pr = pageEl.getBoundingClientRect();
    if (pr.width <= 0 || pr.height <= 0) continue;

    const rects: Rect[] = clientRects
      .filter((r) => intersects(r, pr))
      .map((r) => [
        (r.left - pr.left) / pr.width,
        (r.top - pr.top) / pr.height,
        r.width / pr.width,
        r.height / pr.height,
      ]);

    if (rects.length > 0) out.push(makeRectAnchor(pageIndex, rects, quoteWithin(range, pageEl)));
  }
  return out;
}

/** ¿Alguno de los rectángulos del ancla se superpone con `probe`? */
function anchorOverlaps(anchor: RectAnchor, probe: RectAnchor): boolean {
  if (anchor.page !== probe.page) return false;
  return anchor.rects.some(([ax, ay, aw, ah]) =>
    probe.rects.some(
      ([bx, by, bw, bh]) => ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by,
    ),
  );
}

/**
 * ¿`inner` cae entero adentro de `outer`?
 *
 * Es la pregunta de "¿esto ya estaba marcado?", la que distingue volver a pasar
 * el fibrón para borrar de pasarlo para extender la marca. Cada rectángulo de
 * `inner` tiene que entrar en UNO de `outer`: la unión de varios no se calcula,
 * porque el caso real es seleccionar adentro de una marca existente y no vale
 * la pena la geometría. Si no entra, se trata como pasada nueva — fallar hacia
 * marcar es lo reversible.
 */
export function anchorsCovered(
  outer: readonly RectAnchor[],
  inner: readonly RectAnchor[],
): boolean {
  // Los rectángulos vienen de mediciones distintas (selección vs. render), así
  // que un pelo de diferencia en el borde no tiene que contar como salirse.
  const EPS = 0.002;
  return inner.every((f) =>
    f.rects.every(([bx, by, bw, bh]) =>
      outer.some(
        (o) =>
          o.page === f.page &&
          o.rects.some(
            ([ax, ay, aw, ah]) =>
              ax - EPS <= bx &&
              ay - EPS <= by &&
              ax + aw + EPS >= bx + bw &&
              ay + ah + EPS >= by + bh,
          ),
      ),
    ),
  );
}

/** Resaltados que chocan con alguna de las anclas nuevas. */
export function overlappingHighlights(
  highlights: readonly Highlight[],
  fresh: readonly RectAnchor[],
): Highlight[] {
  return highlights.filter(
    (h) => h.anchor.kind === "rect" && fresh.some((f) => anchorOverlaps(h.anchor as RectAnchor, f)),
  );
}

/**
 * Dibuja en `layer` los resaltados de una página.
 *
 * `layer` va posicionado sobre el canvas y ocupa exactamente la página, así que
 * los rectángulos normalizados se traducen a porcentajes y quedan alineados en
 * cualquier escala sin recalcular nada.
 */
export function paintPage(
  layer: HTMLElement,
  pageIndex: number,
  highlights: readonly Highlight[],
): void {
  layer.replaceChildren();
  for (const h of highlights) {
    if (h.anchor.kind !== "rect" || h.anchor.page !== pageIndex) continue;
    const variant = h.anchor.area ? "pdf-hl-area" : "pdf-hl-text";
    for (const [x, y, w, hh] of h.anchor.rects) {
      const box = document.createElement("div");
      box.className = `pdf-hl ${variant} ${highlightClass(h.color)}`;
      box.style.left = `${x * 100}%`;
      box.style.top = `${y * 100}%`;
      box.style.width = `${w * 100}%`;
      box.style.height = `${hh * 100}%`;
      layer.append(box);
    }
  }
}
